export const DEFAULT_OVERFLOW_PREVIEW_CHARACTERS = 320;

export type OverflowRevealKind = "text" | "path" | "identifier";

export type OverflowPreview = {
  text: string;
  truncated: boolean;
  characterCount: number;
};

type SegmenterConstructor = new (
  locales?: string | string[],
  options?: { granularity: "grapheme" }
) => {
  segment: (value: string) => Iterable<{ segment: string }>;
};

const graphemes = (value: string): string[] => {
  const Segmenter = (Intl as typeof Intl & { Segmenter?: SegmenterConstructor }).Segmenter;
  if (Segmenter) {
    return Array.from(new Segmenter(undefined, { granularity: "grapheme" }).segment(value), (part) => part.segment);
  }
  return Array.from(value);
};

export const overflowCharacterCount = (value: string): number => graphemes(value).length;

export function overflowPreview(
  value: string,
  limit = DEFAULT_OVERFLOW_PREVIEW_CHARACTERS,
  kind: OverflowRevealKind = "text"
): OverflowPreview {
  const characters = graphemes(value);
  const requestedLimit = Number.isFinite(limit)
    ? Math.floor(limit)
    : DEFAULT_OVERFLOW_PREVIEW_CHARACTERS;
  const safeLimit = Math.max(kind === "text" ? 2 : 4, requestedLimit);
  if (characters.length <= safeLimit) {
    return { text: value, truncated: false, characterCount: characters.length };
  }

  const available = safeLimit - 1;
  if (kind === "path" || kind === "identifier") {
    const startRatio = kind === "path" ? .52 : .56;
    const startLength = Math.min(available - 1, Math.max(1, Math.ceil(available * startRatio)));
    const endLength = Math.max(1, available - startLength);
    return {
      text: `${characters.slice(0, startLength).join("")}…${characters.slice(-endLength).join("")}`,
      truncated: true,
      characterCount: characters.length
    };
  }

  return {
    text: `${characters.slice(0, available).join("")}…`,
    truncated: true,
    characterCount: characters.length
  };
}

export type OverflowAnchorRect = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  width: number;
  height: number;
};

export type OverflowPanelSize = { width: number; height: number };
export type OverflowViewport = { width: number; height: number };
export type OverflowPopoverPosition = { top: number; left: number; side: "top" | "right" | "bottom" | "left" };

export function overflowPopoverPosition(
  anchor: OverflowAnchorRect,
  panel: OverflowPanelSize,
  viewport: OverflowViewport,
  gap = 8,
  margin = 12
): OverflowPopoverPosition {
  const availableRight = viewport.width - anchor.right - gap - margin;
  const availableLeft = anchor.left - gap - margin;
  const availableBelow = viewport.height - anchor.bottom - gap - margin;
  const availableAbove = anchor.top - gap - margin;
  const side: OverflowPopoverPosition["side"] = availableRight >= panel.width
    ? "right"
    : availableLeft >= panel.width
      ? "left"
      : availableAbove >= panel.height
        ? "top"
        : availableBelow >= panel.height
          ? "bottom"
          : availableAbove >= availableBelow ? "top" : "bottom";
  const desiredTop = side === "bottom"
    ? anchor.bottom + gap
    : side === "top"
      ? anchor.top - panel.height - gap
      : anchor.top + (anchor.height - panel.height) / 2;
  const desiredLeft = side === "right"
    ? anchor.right + gap
    : side === "left"
      ? anchor.left - panel.width - gap
      : anchor.left + (anchor.width - panel.width) / 2;
  const maxTop = Math.max(margin, viewport.height - panel.height - margin);
  const maxLeft = Math.max(margin, viewport.width - panel.width - margin);

  return {
    top: Math.min(Math.max(margin, desiredTop), maxTop),
    left: Math.min(Math.max(margin, desiredLeft), maxLeft),
    side
  };
}
